import {
  Button,
  DatePicker,
  Dropdown,
  Icon,
  IconButton,
  ICONS_ENUM,
  IDropdownItem
} from '@sanlam-glacier/app-common/components';
import styles from './table-filter.module.scss';
import { useMemo, useState } from 'react';
import { InstructionCategory, Status } from 'modules/case-tracking/types/filtering/filtering.enums';
import { updateStatusOptions } from 'modules/case-tracking/utils/filtering/updateStatusOptions';
import { Stage } from 'modules/case-tracking/types/case-tracking.enums';
import caseTrackingDateInput from './CaseTrackingDateInput';
import { useGetCaseTrackingList } from 'bff/hooks/reporting/CaseTrackingListApi';
import { documentTimestamp } from 'modules/case-tracking/utils/document-timestamp.function';
import { downloadDocumentBackgroundFromBlob } from 'utils/background-promises/application-download';
import { AuthenticatedUser, useAuth } from 'auth';
import { toTitleCase } from 'modules/case-tracking/utils/title-case.function';

type TableFilterProps = {
  handleCategoriesUpdate: (categories: InstructionCategory[]) => void;
  handleStagesUpdate: (stages: Stage[]) => void;
  handleStatusUpdate: (status: Status[]) => void;
  handleDateRangeUpdate: (dateRange: [Date | null, Date | null]) => void;
  downloadDisabled: boolean;
  shouldShowClear: boolean;
};

const TableFilter = ({
  handleCategoriesUpdate,
  handleStagesUpdate,
  handleStatusUpdate,
  handleDateRangeUpdate,
  downloadDisabled,
  shouldShowClear
}: TableFilterProps) => {
  const authApi = useAuth();

  const InstructionsList: InstructionCategory[] = Object.values(InstructionCategory);
  const StageList: Stage[] = Object.values(Stage);
  const allStatus: Status[] = Object.values(Status);

  const stageDropdownItems: IDropdownItem[] = StageList.map((item, index) => {
    return {
      value: item,
      content: item,
      subTitle: item !== Stage.Cancelled ? `Stage ${index + 1}` : undefined
    };
  });

  const [categories, setCategories] = useState<InstructionCategory[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [categorySelected, setCategorySelected] = useState(false);
  const [stageSelected, setStageSelected] = useState(false);
  const [statusSelected, setStatusSelected] = useState(false);
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = range;
  const [pendingDate, setPendingDate] = useState<Date | Date[] | null>(null);

  const [resetKey, setResetKey] = useState(0);
  const clearAll = () => {
    setRange([null, null]);
    setPendingDate(null);
    handleDateRangeUpdate([null, null]);
    setResetKey((prev) => prev + 1);
  };

  const statusOptions = useMemo(
    () => updateStatusOptions(categories, stages, allStatus),
    [categories, stages]
  );

  const processDateSelection = (date: Date | Date[] | null, isOnChange: boolean) => {
    if (Array.isArray(date)) {
      const startDate = date[0] ? new Date(date[0].setHours(0, 0, 0, 0)) : null;
      const endDate = date[1] ? new Date(date[1].setHours(23, 59, 59, 999)) : null;
      const range: [Date | null, Date | null] = [startDate, endDate];
      setRange(range);
      if (!isOnChange) {
        handleDateRangeUpdate(range);
      }
    } else if (date instanceof Date) {
      const selectedDate = new Date(date);
      const startDate = new Date(selectedDate.setHours(0, 0, 0, 0));
      const range: [Date | null, Date | null] = [startDate, startDate];
      setRange(range);
      if (!isOnChange) {
        handleDateRangeUpdate(range);
      }
    } else {
      const range: [Date | null, Date | null] = [null, null];
      setRange(range);
      if (!isOnChange) {
        handleDateRangeUpdate(range);
      }
    }
  };

  const fileName = () => {
    const currentSignedInUser: AuthenticatedUser | undefined = authApi.getSignedInUser();
    const loggedInUserName: string = `${currentSignedInUser?.fullName} ${currentSignedInUser?.familyName}_`;
    const loggedInUserId: string = currentSignedInUser?.guid
      ? ` ${currentSignedInUser?.guid}_`
      : ' ';
    var filterString = 'Instruction List_';
    var catString = categories.map((category) => {
      return ` ${category}_`;
    });
    if (categories.length !== 0) {
      filterString = `${catString} ${filterString}`;
      filterString = filterString.replaceAll(',', '');
    }

    return toTitleCase(
      `Case Tracking_ ${loggedInUserName}${loggedInUserId}${filterString} ${documentTimestamp()}`
    );
  };

  const { mutateAsync: downloadCaseList, isPending: isDownloading } = useGetCaseTrackingList();

  return (
    <div className={styles.container}>
      <IconButton className={styles.caseFilterButton} variant={IconButton.VARIANT.TERTIARY}>
        <Icon icon={ICONS_ENUM.CONTENT_FILTER_LIST} />
      </IconButton>
      <div className={styles.filterContainer}>
        <DatePicker
          className={styles.datePicker}
          date={undefined}
          startDate={startDate ?? undefined}
          endDate={endDate ?? undefined}
          onCalendarClose={() => {
            processDateSelection(pendingDate, false);
          }}
          onChange={(date) => {
            setPendingDate(date);
            processDateSelection(date, true);
          }}
          dateFormat='yyyy/MM/dd'
          helperTextVisibleOnOpen
          label='Range Select'
          maxDate={new Date()}
          minDate={new Date('2015-09-05T09:10:44.748Z')}
          shouldCloseOnSelect={false}
          rangeSelect
          customInput={caseTrackingDateInput(startDate, endDate)}
          hidePopperCTAs={false}
          controlled={true}
        />
        {/* Instructions */}
        <Dropdown
          key={`category-dropdown-${resetKey}`}
          className={styles.filter}
          placeholder={categorySelected ? '' : 'Instruction type'}
          multiSelect={true}
          chipLimit={1}
          items={InstructionsList}
          maxHeight={224}
          onChange={(items) => {
            const categories = items as InstructionCategory[];
            setCategorySelected(categories.length > 0);
            handleCategoriesUpdate(categories);
            setCategories(categories);
          }}
          popperPlacement={Dropdown.POSITION.BOTTOM}
          inputVariant={Dropdown.VARIANT.TABLE}
        />
        {/* Stages */}
        <Dropdown
          key={`stage-dropdown-${resetKey}`}
          className={styles.filter}
          placeholder={stageSelected ? '' : 'Stage'}
          multiSelect={true}
          chipLimit={1}
          items={stageDropdownItems}
          maxHeight={224}
          onChange={(items) => {
            const stageItems = items as IDropdownItem[];
            setStageSelected(stageItems.length > 0);
            const stages = stageItems.map((item) => item.value as Stage);
            handleStagesUpdate(stages);
            setStages(stages);
          }}
          popperPlacement={Dropdown.POSITION.BOTTOM}
          inputVariant={Dropdown.VARIANT.TABLE}
        />
        <div className={styles.break} />
        {/* Statusses */}
        <Dropdown
          key={`status-dropdown-${resetKey}`}
          className={styles.filter}
          placeholder={statusSelected ? '' : 'Status'}
          multiSelect={true}
          chipLimit={1}
          items={statusOptions}
          maxHeight={224}
          onChange={(items) => {
            const statusItems = items as IDropdownItem[];
            setStatusSelected(statusItems.length > 0);
            const status = statusItems.map((item) => item.value as Status);
            handleStatusUpdate(status);
          }}
          popperPlacement={Dropdown.POSITION.BOTTOM}
          inputVariant={Dropdown.VARIANT.TABLE}
        />
        {shouldShowClear && (
          <Button
            className={styles.clearButton}
            startIcon={<Icon icon={ICONS_ENUM.NAVIGATION_REFRESH} />}
            variant={Button.VARIANT.TERTIARY}
            size={Button.SIZE.SMALL}
            onClick={() => {
              clearAll();
            }}
          >
            Clear filters
          </Button>
        )}
        <Button
          data-testid='caseTrackingDownloadCaseList'
          disabled={isDownloading || downloadDisabled}
          className={styles.downloadButton}
          startIcon={<Icon icon={ICONS_ENUM.DOCUMENTS_EXCEL_DOCUMENT} />}
          variant={Button.VARIANT.SECONDARY}
          size={Button.SIZE.SMALL}
          onClick={() => {
            downloadDocumentBackgroundFromBlob({
              downloadFn: async () => {
                const result = await downloadCaseList();
                if (!result) return;
                return {
                  blob: result,
                  fileName: fileName()
                };
              },
              wording: {
                message:
                  'Case tracking overview Excel sheet will be available in your downloads folder when the download is complete.',
                title: 'Excel sheet downloading...',
                successMessage: 'Case Tracking overview Excel sheet successfully downloaded.',
                errorMessage: 'Case Tracking overview Excel sheet download failed.',
                promiseCompleteTitle: 'Excel document download'
              },
              toastId: 'caseTracking:caseListOverview'
            });
          }}
        >
          Download Excel
        </Button>
      </div>
    </div>
  );
};

export default TableFilter;
